import {getCart} from './payload';
import {createNumberInput} from './numberInput';
import {getOpenedDropdown} from './dropdown';
import {renderCartButton} from './layoutHeaderCartButton';

const INIT_HREF_ATTR = 'data-init-href';
const REMOVE_HREF_TEMPLATE_ATTR = 'data-remove-href';
const CHANGE_COUNT_HREF_TEMPLATE_ATTR = 'data-change-count-href';
const PRODUCT_ID_ATTR = 'data-product-id';
const SUPPLY_ID_ATTR = 'data-supply-id';

const PRODUCT_ROW_CLASS = 'cartItemRow';
const QUANTITY_INPUT_SELECTOR = '.cartItemRow__quantity > input[type="number"]';
const NUMBER_INPUT_SELECTOR = '.numberInput input[name="count"]';
const REMOVE_BUTTON_CLASS = '.cartItemRow__remove';
const DROPDOWN_CONTENT_CLASS = 'cartDropdown__content';
const DROPDOWN_SPINNER_CLASS = 'cartDropdown__spinnerWrapper';
const LAYOUT_HEADER_SHOPPING_CART_BUTTON_SELECTOR = '.layoutHeaderMainBar__buttonCell.--shoppingCart > :first-child, .mlpartsHeader__navItem.--shoppingCart';
const CONTINUE_BUTTON_CLASS = 'cartDropdown__nav__continue';
const MODAL_CLASS = '--modal';
const DROPDOWN_CLASS = '--dropdown';

const PLACEHOLDER_PRODUCT_ID = '_productId_';
const PLACEHOLDER_SUPPLY_ID = '_supplyId_';
const PLACEHOLDER_COUNT = '_count_';

const CART_ACTIONS = {
    REMOVE: 'remove',
    CHANGE_COUNT: 'changeCount',
};

class CartDropdownElement extends HTMLElement {
    constructor() {
        super();
        this.newCartCount = 0;
        this.cartCount = null;
        this.handleInit = this.handleInit.bind(this);
        this.toggleLoadingState = this.toggleLoadingState.bind(this);
        this.initProductActions = this.initProductActions.bind(this);
        this.handleRemoveItem = this.handleRemoveItem.bind(this);
        this.handleChangeCount = this.handleChangeCount.bind(this);
        this.handleCartPayloadEvent = this.handleCartPayloadEvent.bind(this);
        this.cleanupBeforeReplacement = this.cleanupBeforeReplacement.bind(this);
        this.numberInputListeners = new WeakMap();
        this.removeButtonListeners = new WeakMap();
        this.listenerAdded = false;
        this.isInitialized = false;
    }

    connectedCallback() {
        this.initHref = this.getAttribute(INIT_HREF_ATTR);
        this.removeHrefTemplate = this.getAttribute(REMOVE_HREF_TEMPLATE_ATTR);
        this.changeCountHrefTemplate = this.getAttribute(CHANGE_COUNT_HREF_TEMPLATE_ATTR);

        // noinspection OverlyComplexBooleanExpressionJS
        if (this.initHref && this.removeHrefTemplate && this.changeCountHrefTemplate && this.className.includes(DROPDOWN_CLASS)) {
            const button = document.querySelector(LAYOUT_HEADER_SHOPPING_CART_BUTTON_SELECTOR);
            if (button && !this.listenerAdded) {
                button.addEventListener('click', this.handleInit);
                this.listenerAdded = true;
            }
        } else if (this.changeCountHrefTemplate && !this.isInitialized && this.className.includes(MODAL_CLASS)) {
            this.initProductActions([CART_ACTIONS.CHANGE_COUNT]);
            this.isInitialized = true;
        }

        this.initContinueButton();

        document.addEventListener('kvik_event_ajax_payload', this.handleCartPayloadEvent);
    }

    disconnectedCallback() {
        const button = document.querySelector(LAYOUT_HEADER_SHOPPING_CART_BUTTON_SELECTOR);
        if (button && this.listenerAdded) {
            button.removeEventListener('click', this.handleInit);
            this.listenerAdded = false;
        }
        this.isInitialized = false;
        document.removeEventListener('kvik_event_ajax_payload', this.handleCartPayloadEvent);
    }

    initContinueButton() {
        const continueButtons = this.getElementsByClassName(CONTINUE_BUTTON_CLASS);
        if (continueButtons.length) {
            continueButtons[0].addEventListener('click', () => {
                window.kvik_modal?.destroy();
                getOpenedDropdown()?.close();
            });
        }
    }

    handleInit() {
        if (this.newCartCount === this.cartCount) {
            return;
        }

        this.toggleLoadingState(true);
        this.processCartAction(this.initHref);
    }

    handleReplaceCart(data) {
        if (data.html) {
            const parent = this.parentNode;
            this.cleanupBeforeReplacement();
            this.outerHTML = data.html;
            const newElement = parent.querySelector(this.tagName);
            newElement.cartCount = data.cart.count;
            newElement.newCartCount = data.cart.count;
            newElement.initProductActions();
            window.KMPayload.cart = data.cart;
            renderCartButton(data.cart);
            requestAnimationFrame(() => {
                document.dispatchEvent(new CustomEvent('kvik_event_ajax_payload'));
            });
        }
    }

    cleanupBeforeReplacement() {
        const productRows = this.getElementsByClassName(PRODUCT_ROW_CLASS);
        for (const productRow of productRows) {
            const numberInput = productRow.querySelector(NUMBER_INPUT_SELECTOR);
            if (numberInput && this.numberInputListeners.has(numberInput)) {
                const handleChange = this.numberInputListeners.get(numberInput);
                numberInput.removeEventListener('change', handleChange);
                this.numberInputListeners.delete(numberInput);
            }

            const deleteButton = productRow.querySelector(REMOVE_BUTTON_CLASS);
            if (deleteButton && this.removeButtonListeners.has(deleteButton)) {
                deleteButton.removeEventListener('click', this.removeButtonListeners.get(deleteButton));
                this.removeButtonListeners.delete(deleteButton);
            }
        }
    }

    handleCartPayloadEvent() {
        this.newCartCount = getCart()?.count ?? 0;
    }

    initProductActions(actions = Object.values(CART_ACTIONS)) {
        const productRows = this.getElementsByClassName(PRODUCT_ROW_CLASS);

        const productIds = this.getProductIdsFromRows(productRows);
        this.initActionsForRows(productRows, actions, productIds);
    }

    getProductIdsFromRows(productRows) {
        const productIds = [];
        for (const productRow of productRows) {
            const productId = productRow.getAttribute(PRODUCT_ID_ATTR);
            productIds.push(productId);
        }
        return productIds;
    }

    initActionsForRows(productRows, actions, productIds) {
        for (const productRow of productRows) {
            const productId = productRow.getAttribute(PRODUCT_ID_ATTR);
            const supplyId = productRow.getAttribute(SUPPLY_ID_ATTR);

            this.createNumberInputForRow(productRow);
            this.addChangeCountAction(productRow, actions, productId, supplyId, productIds);
            this.addRemoveAction(productRow, actions, productId, supplyId);
        }
    }

    createNumberInputForRow(productRow) {
        const originalInput = productRow.querySelector(QUANTITY_INPUT_SELECTOR);
        if (originalInput) {
            createNumberInput(originalInput);
        }
    }

    addChangeCountAction(productRow, actions, productId, supplyId, productIds) {
        if (actions.includes(CART_ACTIONS.CHANGE_COUNT)) {
            const numberInput = productRow.querySelector(NUMBER_INPUT_SELECTOR);
            if (numberInput && !this.numberInputListeners.has(numberInput)) {
                const handleChange = (event) => {
                    const newCount = event.target.value;
                    this.handleChangeCount(productId, supplyId, newCount, productIds);
                };

                numberInput.addEventListener('change', handleChange);
                this.numberInputListeners.set(numberInput, handleChange);
            }
        }
    }

    addRemoveAction(productRow, actions, productId, supplyId) {
        if (actions.includes(CART_ACTIONS.REMOVE)) {
            const deleteButton = productRow.querySelector(REMOVE_BUTTON_CLASS);
            if (deleteButton) {
                if (this.removeButtonListeners.has(deleteButton)) {
                    deleteButton.removeEventListener('click', this.removeButtonListeners.get(deleteButton));
                }

                const handleRemove = () => this.handleRemoveItem(productId, supplyId);
                deleteButton.addEventListener('click', handleRemove);
                this.removeButtonListeners.set(deleteButton, handleRemove);
            }
        }
    }

    handleRemoveItem(productId, supplyId) {
        this.processCartAction(this.removeHrefTemplate, productId, supplyId);
    }

    handleChangeCount(productId, supplyId, count, productIds) {
        this.processCartAction(this.changeCountHrefTemplate, productId, supplyId, count, productIds);
    }

    processCartAction(url, productId = null, supplyId = null, count = null, productIds = []) {
        const href = this.replacePlaceholders(url, productId, supplyId, count, productIds);

        this.toggleLoadingState(true);

        const config = {
            method: 'POST',
            mode: 'same-origin',
            headers: {'X-Requested-With': 'XMLHttpRequest'},
        };

        fetch(href, config)
            .then(response => response.ok ? response.json() : null)
            .then(data => {
                if (data) {
                    this.handleReplaceCart(data);
                }
            })
            .catch(error => {
                console.error(error);
                this.toggleLoadingState(false);
            });
    }

    toggleLoadingState(isLoading) {
        const content = this.getElementsByClassName(DROPDOWN_CONTENT_CLASS)[0];
        const spinner = this.getElementsByClassName(DROPDOWN_SPINNER_CLASS)[0];

        if (content) {
            content.hidden = isLoading;
        }
        if (spinner) {
            spinner.hidden = !isLoading;
        }
    }

    replacePlaceholders(url, productId, supplyId, count = null, productIds = []) {
        let updatedUrl = url.replace(PLACEHOLDER_PRODUCT_ID, productId);

        if (supplyId) {
            updatedUrl = updatedUrl.replace(PLACEHOLDER_SUPPLY_ID, supplyId);
        } else {
            updatedUrl = updatedUrl.replace(/&?(cartDropdown-supplyId|addToCart-supplyId|supplyId)=[^&]*/, '');
        }

        if (count !== null) {
            updatedUrl = updatedUrl.replace(PLACEHOLDER_COUNT, count);
        } else {
            updatedUrl = updatedUrl.replace(/&?count=[^&]*/, '');
        }

        if (updatedUrl.includes('productIds')) {
            updatedUrl = productIds.length === 0
                ? updatedUrl.replace(/&?productIds=[^&]*/, '')
                : updatedUrl.replace(/productIds=[^&]*/, `productIds=${productIds.join(',')}`);
        }

        return updatedUrl;
    }
}

window.customElements.define('kvik-cart-dropdown', CartDropdownElement);
